import React, { useEffect, useRef, useState } from "react"

const FOCUSABLE = "button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), a[href], [tabindex]:not([tabindex='-1'])"

// A dialog of the Events Planner, over the calendar and the editor: the focus goes in, stays in (Tab and Shift+Tab cycle) and comes
// back on closing; Esc closes it and stops there (the quick peek and the editor behind it stay open). With `dirty` typed text, closing
// asks first, as the editor does (CP lesson 2026-09-29). The footer holds the close button (`closeLabel`) and the dialog's `actions`.
export default function PlannerDialog({ title, sub, wide, dirty, onClose, closeLabel = "Cancel", actions, children }) {
  const panel = useRef(null)
  const [leaving, setLeaving] = useState(false)
  const close = () => (dirty ? setLeaving(true) : onClose())
  useEffect(() => {
    const opener = document.activeElement
    const first = panel.current?.querySelector("textarea, input, select, button:not(.ep-icon-button)")
    ;(first || panel.current)?.focus()
    return () => { if (opener && document.contains(opener)) opener.focus() }
  }, [])
  const keys = event => {
    if (event.key === "Escape") {
      // The page's own Esc listeners (the quick peek, the editor) see it handled.
      event.preventDefault()
      close()
      return
    }
    if (event.key !== "Tab") return
    const focusable = [...panel.current.querySelectorAll(FOCUSABLE)]
    if (!focusable.length) return
    const first = focusable[0], last = focusable[focusable.length - 1]
    if (event.shiftKey && [first, panel.current].includes(document.activeElement)) { event.preventDefault(); last.focus() }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
  }
  return <>
    <div className="ep-scrim ep-dialog-scrim" aria-hidden="true" onClick={close} />
    <div role="dialog" aria-modal="true" aria-label={title} className={"ep-dialog" + (wide ? " is-wide" : "")} ref={panel} tabIndex={-1} onKeyDown={keys}>
      <header className="ep-dialog-head">
        <div className="ep-stack"><h2>{title}</h2>{sub && <span className="ep-meta">{sub}</span>}</div>
        <button type="button" className="ep-icon-button" aria-label="Close" onClick={close}><i className="bx bx-x" aria-hidden="true" /></button>
      </header>
      <div className="ep-dialog-body">{children}</div>
      <footer className="ep-dialog-foot">
        {leaving ? <div className="ep-confirm" role="alertdialog" aria-label="Unsaved changes">
          <span>Discard the unsaved changes?</span>
          <button type="button" className="btn btn-danger btn-sm" onClick={onClose}>Discard changes</button>
          <button type="button" className="btn btn-light btn-sm" onClick={() => setLeaving(false)}>Keep editing</button>
        </div> : <>
          <button type="button" className="btn btn-light" onClick={close}>{closeLabel}</button>
          {actions}
        </>}
      </footer>
    </div>
  </>
}
